import React, { useState, useEffect } from "react";
import { FileSignature, Download, Check, X, Send, MessageSquare, Clock, ChevronRight, Loader2, AlertCircle, ScrollText, FileCheck2, ShieldCheck, RefreshCw, PenLine } from "lucide-react";
import { authApi } from "./authApi";
import { API } from "./api";

const STATUS_STYLE = {
  received: "text-amber-300 border-amber-400/40 bg-amber-500/10",
  transmitted: "text-sky-300 border-sky-400/40 bg-sky-500/10",
  precisions: "text-purple-300 border-purple-400/40 bg-purple-500/10",
  accepted: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10",
  refused: "text-red-300 border-red-400/40 bg-red-500/10",
  negotiating: "text-[#C19A6B] border-[#C19A6B]/40 bg-[#C19A6B]/10",
  counter_sent: "text-[#C19A6B] border-[#C19A6B]/40 bg-[#C19A6B]/10",
  expired: "text-zinc-400 border-white/15 bg-white/5",
};
const fmt = (n) => { try { return `${parseInt(n).toLocaleString("fr-FR")} €`; } catch { return n; } };
const FIN_LABELS = { comptant: "Comptant", pret: "Prêt bancaire", mixte: "Mixte (apport + prêt)", autre: "Autre" };
const finLabel = (v) => FIN_LABELS[v] || v;
const dt = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function Badge({ o }) {
  const cls = o.expired ? STATUS_STYLE.expired : (STATUS_STYLE[o.status] || "text-zinc-300 border-white/15");
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${cls}`}>{o.expired ? "Expirée" : o.status_label}</span>;
}

const DS_STATUS = {
  sent: { t: "Envoyée pour signature", c: "text-sky-300 border-sky-400/40 bg-sky-500/10" },
  delivered: { t: "En cours de signature", c: "text-sky-300 border-sky-400/40 bg-sky-500/10" },
  completed: { t: "Signée par tous ✓", c: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10" },
  declined: { t: "Refusée par un signataire", c: "text-red-300 border-red-400/40 bg-red-500/10" },
  voided: { t: "Annulée", c: "text-zinc-400 border-white/15 bg-white/5" },
};

function DocuSignBlock({ id, kind, rec, sellerDefault, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [seller, setSeller] = useState({ email: sellerDefault?.email || "", name: sellerDefault?.name || "" });
  const [live, setLive] = useState(rec || null);
  useEffect(() => { setLive(rec || null); }, [rec]);
  const send = async () => {
    if (!seller.email.trim()) { setErr("L'e-mail du vendeur est requis."); return; }
    setErr(""); setBusy(true);
    try { const o = await authApi.docusignSend(id, kind, { seller_email: seller.email, seller_name: seller.name }); setLive((o.docusign || {})[kind]); setOpen(false); onChange && onChange(o); }
    catch (e) { setErr(e.detail || e.message || "Envoi impossible."); }
    setBusy(false);
  };
  const refresh = async () => {
    setBusy(true); setErr("");
    try { const s = await authApi.docusignStatus(id, kind); setLive((p) => ({ ...(p || {}), status: s.status, signers: s.signers || (p && p.signers) })); }
    catch (e) { setErr(e.detail || e.message || ""); }
    setBusy(false);
  };
  const st = live?.status;
  const meta = DS_STATUS[st];
  const label = kind === "offer" ? "l'offre d'achat" : kind === "compromis" ? "le compromis" : "la promesse";

  if (!live?.envelope_id) {
    return (
      <div className="border border-[#C19A6B]/25 bg-[#C19A6B]/5 p-3" data-testid={`docusign-${kind}`}>
        <p className="text-[11px] uppercase tracking-wider text-[#C19A6B] mb-1 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Signature certifiée DocuSign</p>
        <p className="text-[11px] text-zinc-500 mb-2">Envoyer {label} en signature électronique certifiée (acquéreur → vendeur → conseiller, chacun reçoit un e-mail).</p>
        {!open ? (
          <button onClick={() => setOpen(true)} disabled={busy} data-testid={`docusign-send-open-${kind}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-[#C19A6B] hover:bg-[#d1af84] text-white px-3 py-2"><PenLine className="w-4 h-4" /> Envoyer en signature</button>
        ) : (
          <div className="space-y-2">
            <div className="grid sm:grid-cols-2 gap-2">
              <input value={seller.name} onChange={(e) => setSeller({ ...seller, name: e.target.value })} placeholder="Nom du vendeur" className="bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid={`docusign-seller-name-${kind}`} />
              <input type="email" value={seller.email} onChange={(e) => setSeller({ ...seller, email: e.target.value })} placeholder="E-mail du vendeur *" className="bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid={`docusign-seller-email-${kind}`} />
            </div>
            <div className="flex gap-2">
              <button onClick={send} disabled={busy} data-testid={`docusign-send-${kind}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-[#C19A6B] hover:bg-[#d1af84] text-white px-3 py-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Envoyer</button>
              <button onClick={() => setOpen(false)} className="text-xs uppercase tracking-wider border border-white/15 text-zinc-400 px-3 py-2">Annuler</button>
            </div>
          </div>
        )}
        {err && <p className="text-[11px] text-red-400 mt-2">{err}</p>}
      </div>
    );
  }
  return (
    <div className="border border-white/10 p-3" data-testid={`docusign-${kind}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] uppercase tracking-wider text-[#C19A6B] flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Signature DocuSign — {label}</p>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${meta?.c || "text-zinc-300 border-white/15"}`} data-testid={`docusign-status-${kind}`}>{meta?.t || st}</span>
      </div>
      {live.signers && live.signers.length > 0 && (
        <div className="mt-2 space-y-1">
          {live.signers.map((s, i) => (
            <div key={i} className="flex items-center justify-between text-[11px]"><span className="text-zinc-300">{s.name || s.email}</span><span className={s.status === "completed" ? "text-emerald-400" : "text-zinc-500"}>{s.status === "completed" ? "Signé ✓" : s.status}</span></div>
          ))}
        </div>
      )}
      <div className="flex gap-2 mt-3">
        <button onClick={refresh} disabled={busy} data-testid={`docusign-refresh-${kind}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-white/15 text-zinc-300 px-3 py-2 hover:border-[#C19A6B] hover:text-[#C19A6B]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Actualiser</button>
        {st === "completed" && <a href={`${API}/offers/${id}/docusign/${kind}/download`} target="_blank" rel="noopener noreferrer" data-testid={`docusign-download-${kind}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-emerald-400/40 text-emerald-300 px-3 py-2 hover:bg-emerald-500/10"><Download className="w-4 h-4" /> Document signé + certificat</a>}
      </div>
      {err && <p className="text-[11px] text-red-400 mt-2">{err}</p>}
    </div>
  );
}

function OfferDetail({ id, onClose, onChange }) {
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [seller, setSeller] = useState({ email: "", name: "" });
  const [showTransmit, setShowTransmit] = useState(false);
  const [dsOn, setDsOn] = useState(false);
  useEffect(() => { authApi.offerAdvisorDetail(id).then((d) => { setO(d); setComment(d.advisor?.internal_comment || ""); }).catch(() => {}); }, [id]);
  useEffect(() => { authApi.docusignAvailable().then((d) => setDsOn(!!d.configured)).catch(() => {}); }, []);
  if (!o) return null;
  const act = async (action, extra = {}) => {
    setBusy(true);
    try { const d = await authApi.offerAdvisorAction(id, { action, ...extra }); setO(d); onChange && onChange(); } catch (_) {} setBusy(false);
  };
  const b = o.buyer || {}; const of = o.offer || {};
  const closed = ["refused", "accepted"].includes(o.status);
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="offer-detail-modal">
      <div className="bg-[#0A0A0C] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><FileSignature className="w-5 h-5 text-[#C19A6B]" /><h3 className="text-lg font-light text-white">Offre {o.ref}</h3><Badge o={o} /></div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-4">{o.listing_title} — {o.listing_city}</p>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div className="border border-white/10 p-3 text-sm space-y-1">
            <p className="text-[11px] uppercase tracking-wider text-[#C19A6B] mb-1">Acquéreur</p>
            <div className="text-white">{b.first_name} {b.last_name}</div>
            <div className="text-zinc-400 text-xs">{b.email}</div>
            <div className="text-zinc-400 text-xs">{b.phone}</div>
            {b.address && <div className="text-zinc-500 text-xs">{b.address}</div>}
          </div>
          <div className="border border-white/10 p-3 text-sm space-y-1">
            <p className="text-[11px] uppercase tracking-wider text-[#C19A6B] mb-1">Offre</p>
            <div className="flex justify-between"><span className="text-zinc-500">Montant</span><span className="text-white">{fmt(of.amount)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Financement</span><span className="text-white">{finLabel(of.financing)}</span></div>
            {of.deposit_amount && <div className="flex justify-between"><span className="text-zinc-500">Apport</span><span className="text-white">{fmt(of.deposit_amount)}</span></div>}
            {of.validity_date && <div className="flex justify-between"><span className="text-zinc-500">Validité</span><span className="text-white">{of.validity_date}</span></div>}
          </div>
        </div>
        {of.conditions && <div className="border border-white/10 p-3 mb-4 text-sm"><p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Conditions</p><p className="text-zinc-300">{of.conditions}</p></div>}

        <a href={`${API}/offers/${id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="offer-pdf-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-2 mb-4"><Download className="w-4 h-4" /> Télécharger le PDF signé</a>

        {/* Signature certifiée DocuSign — offre d'achat */}
        {dsOn && <div className="mb-4"><DocuSignBlock id={id} kind="offer" rec={o.docusign?.offer} sellerDefault={o.seller} onChange={setO} /></div>}

        {/* Avant-contrats — offre acceptée */}
        {o.status === "accepted" && (
          <div className="border border-emerald-400/20 bg-emerald-500/5 p-3 mb-4" data-testid="offer-contracts">
            <p className="text-[11px] uppercase tracking-wider text-emerald-300 mb-1 flex items-center gap-1.5"><FileCheck2 className="w-3.5 h-3.5" /> Offre acceptée — générer l'avant-contrat</p>
            <p className="text-[11px] text-zinc-500 mb-3">Projet reprenant automatiquement le bien, les parties, le prix convenu et les conditions. À faire valider par le notaire.</p>
            <div className="flex flex-wrap gap-2">
              <a href={`${API}/offers/${id}/contract/compromis`} target="_blank" rel="noopener noreferrer" data-testid="offer-compromis-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-2"><ScrollText className="w-4 h-4" /> Compromis de vente</a>
              <a href={`${API}/offers/${id}/contract/promesse`} target="_blank" rel="noopener noreferrer" data-testid="offer-promesse-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-2"><ScrollText className="w-4 h-4" /> Promesse de vente</a>
            </div>
            {dsOn && (
              <div className="mt-3 space-y-2">
                <DocuSignBlock id={id} kind="compromis" rec={o.docusign?.compromis} sellerDefault={o.seller} onChange={setO} />
                <DocuSignBlock id={id} kind="promesse" rec={o.docusign?.promesse} sellerDefault={o.seller} onChange={setO} />
              </div>
            )}
          </div>
        )}

        {/* Historique */}
        <div className="border border-white/10 p-3 mb-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Historique</p>
          <div className="space-y-1.5">
            {(o.history || []).map((h, i) => (
              <div key={i} className="flex items-start gap-2 text-xs"><Clock className="w-3 h-3 text-zinc-600 mt-0.5" /><div><span className="text-zinc-300">{h.note}</span><span className="text-zinc-600 ml-2">{dt(h.at)}</span></div></div>
            ))}
          </div>
        </div>

        {/* Commentaire interne */}
        <div className="mb-4">
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Commentaire interne (privé)</label>
          <div className="flex gap-2">
            <input value={comment} onChange={(e) => setComment(e.target.value)} className="flex-1 bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="offer-comment-input" placeholder="Note visible uniquement par vous" />
            <button onClick={() => act("comment", { comment })} disabled={busy} className="text-xs uppercase tracking-wider border border-white/15 text-zinc-300 px-3 hover:border-[#C19A6B] hover:text-[#C19A6B]" data-testid="offer-comment-save"><MessageSquare className="w-4 h-4" /></button>
          </div>
        </div>

        {o.advisor?.refuse_reason && <p className="text-xs text-red-400 mb-4">Motif de refus : {o.advisor.refuse_reason}</p>}

        {/* Actions */}
        {!closed && (
          <div className="space-y-3 border-t border-white/10 pt-4">
            <div className="flex flex-wrap gap-2">
              {o.status === "received" && !showTransmit && <button onClick={() => setShowTransmit(true)} disabled={busy} data-testid="offer-transmit" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm uppercase tracking-wider px-4 py-2.5"><Send className="w-4 h-4" /> Transmettre au vendeur</button>}
              <button onClick={() => { const c = window.prompt("Quelles précisions demander à l'acquéreur ?"); if (c) act("request_precisions", { comment: c }); }} disabled={busy} data-testid="offer-precisions" className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 text-sm uppercase tracking-wider px-4 py-2.5 hover:border-[#C19A6B] hover:text-[#C19A6B]"><MessageSquare className="w-4 h-4" /> Demander des précisions</button>
            </div>
            {showTransmit && o.status === "received" && (
              <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-3 space-y-2" data-testid="transmit-form">
                <p className="text-[11px] uppercase tracking-wider text-[#C19A6B]">Coordonnées du vendeur (propriétaire)</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <input value={seller.name} onChange={(e) => setSeller({ ...seller, name: e.target.value })} placeholder="Nom du vendeur" className="bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="seller-name-input" />
                  <input type="email" value={seller.email} onChange={(e) => setSeller({ ...seller, email: e.target.value })} placeholder="E-mail du vendeur *" className="bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="seller-email-input" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { if (!seller.email.trim()) return; act("transmit", { seller_email: seller.email, seller_name: seller.name }); }} disabled={busy || !seller.email.trim()} data-testid="transmit-confirm" className="bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm uppercase tracking-wider px-4 py-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer au vendeur"}</button>
                  <button onClick={() => setShowTransmit(false)} className="border border-white/15 text-zinc-400 text-sm uppercase tracking-wider px-4 py-2">Annuler</button>
                </div>
                <p className="text-[10px] text-zinc-500">Un lien sécurisé sera envoyé au vendeur pour consulter, accepter, refuser ou contre-proposer.</p>
              </div>
            )}
            <div className="flex gap-2">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif de refus" className="flex-1 bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-red-400" data-testid="offer-refuse-reason" />
              <button onClick={() => { if (!reason.trim()) return; if (window.confirm("Refuser de transmettre cette offre ?")) act("refuse", { reason }); }} disabled={busy} data-testid="offer-refuse" className="inline-flex items-center gap-2 border border-red-400/40 text-red-300 text-sm uppercase tracking-wider px-4 py-2.5 hover:bg-red-500/10"><X className="w-4 h-4" /> Refuser</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OffersTab() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => authApi.offersMine().then(setData).catch(() => setData({ offers: [], stats: {} }));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="py-12 text-center text-sm text-[#C19A6B] uppercase tracking-[0.25em] animate-pulse">Chargement…</div>;
  const s = data.stats || {};
  return (
    <div className="space-y-6" data-testid="offers-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[{ l: "Reçues", v: s.received || 0 }, { l: "Transmises", v: s.transmitted || 0 }, { l: "Acceptées", v: s.accepted || 0 }, { l: "Refusées", v: s.refused || 0 }].map((x) => (
          <div key={x.l} className="border border-white/10 p-4"><div className="text-2xl font-light text-white">{x.v}</div><div className="text-[11px] text-zinc-500 mt-1">{x.l}</div></div>
        ))}
      </div>

      {data.offers.length === 0 ? (
        <div className="border border-white/10 p-10 text-center" data-testid="offers-empty">
          <FileSignature className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">Aucune offre reçue pour le moment.</p>
          <p className="text-xs text-zinc-600 mt-1">Les offres officielles déposées sur vos annonces apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="offers-list">
          {data.offers.map((o) => (
            <button key={o.id} onClick={() => setOpen(o.id)} data-testid={`offer-row-${o.id}`} className="w-full flex items-center gap-4 border border-white/10 px-4 py-3 hover:border-[#C19A6B]/40 transition-colors text-left">
              {!o.read_by_advisor && <span className="w-2 h-2 rounded-full bg-[#C19A6B] shrink-0" title="Non lue" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-white truncate">{o.buyer?.first_name} {o.buyer?.last_name}</span>
                  <Badge o={o} />
                </div>
                <div className="text-xs text-zinc-500 truncate">{o.ref} · {o.listing_title} · {dt(o.created_at)}</div>
              </div>
              <span className="text-sm text-[#C19A6B] font-medium">{fmt(o.offer?.amount)}</span>
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </button>
          ))}
        </div>
      )}

      {open && <OfferDetail id={open} onClose={() => setOpen(null)} onChange={load} />}
    </div>
  );
}
